import { useMemo, useState } from 'react';
import { CHANGELOG_ENTRIES, CURRENT_VERSION, TYPE_META, type ChangelogType } from '../lib/changelog';
import { formatDate } from '../lib/format';

const FILTERS: Array<{ value: 'all' | ChangelogType; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'feature', label: 'Nuevas funciones' },
  { value: 'improvement', label: 'Mejoras' },
  { value: 'fix', label: 'Correcciones' },
  { value: 'security', label: 'Seguridad' },
];

interface MonthGroup {
  label: string;
  entries: typeof CHANGELOG_ENTRIES;
}

const monthFormatter = new Intl.DateTimeFormat('es-VE', {
  timeZone: 'America/Caracas',
  month: 'long',
  year: 'numeric',
});

const monthKeyFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Caracas',
  year: 'numeric',
  month: '2-digit',
});

function parseEntryDate(iso: string): Date {
  return new Date(`${iso}T12:00:00-04:00`);
}

function groupByMonth(entries: typeof CHANGELOG_ENTRIES): MonthGroup[] {
  const groups = new Map<string, MonthGroup>();
  for (const e of entries) {
    const d = parseEntryDate(e.date);
    const key = monthKeyFormatter.format(d);
    const label = monthFormatter.format(d);
    if (!groups.has(key)) groups.set(key, { label, entries: [] });
    groups.get(key)!.entries.push(e);
  }
  return Array.from(groups.values());
}

export default function Changelog() {
  const [filter, setFilter] = useState<'all' | ChangelogType>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return CHANGELOG_ENTRIES.filter((e) => {
      if (filter !== 'all' && e.type !== filter) return false;
      if (!term) return true;
      return (
        e.title.toLowerCase().includes(term) ||
        e.summary.toLowerCase().includes(term) ||
        (e.details ?? []).some((d) => d.toLowerCase().includes(term))
      );
    });
  }, [filter, search]);

  const grouped = groupByMonth(filtered);
  const counts = CHANGELOG_ENTRIES.reduce<Record<string, number>>((acc, e) => {
    acc[e.type] = (acc[e.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-4xl">
      <header className="mb-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              Mejoras de la plataforma
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">
              Histórico de las funcionalidades nuevas, mejoras y correcciones que aplicamos al sistema. Las entradas más recientes aparecen primero.
            </p>
          </div>
          <div className="bg-slate-900 text-white rounded-2xl px-5 py-4 shrink-0 shadow-lg">
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber-400 font-bold">
              Versión actual
            </div>
            <div className="text-2xl md:text-3xl font-bold font-mono mt-1">
              v{CURRENT_VERSION}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <SummaryChip label="Total" count={CHANGELOG_ENTRIES.length} className="bg-slate-900 text-white" />
          {Object.entries(TYPE_META).map(([key, meta]) =>
            counts[key] ? (
              <SummaryChip
                key={key}
                label={meta.label}
                count={counts[key] ?? 0}
                className={meta.badgeClass}
              />
            ) : null,
          )}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar en el historial…"
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
          <div className="flex items-center gap-1 flex-wrap">
            {FILTERS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  filter === opt.value
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <details className="mt-5 text-xs text-slate-600 bg-slate-100/70 rounded-lg px-4 py-3">
          <summary className="cursor-pointer font-semibold text-slate-700 select-none">
            ¿Qué significa el número de versión?
          </summary>
          <div className="mt-2 leading-relaxed space-y-1">
            <p>
              Usamos el formato <span className="font-mono">X.Y.Z</span> (SemVer):
            </p>
            <ul className="space-y-1 ml-4">
              <li>
                <strong className="font-mono">X</strong> — Cambios estructurales grandes o rediseños.
              </li>
              <li>
                <strong className="font-mono">Y</strong> — Una nueva función entró en producción.
              </li>
              <li>
                <strong className="font-mono">Z</strong> — Mejoras menores, correcciones o parches de seguridad.
              </li>
            </ul>
            <p className="pt-1">
              Hoy estamos en <span className="font-mono font-bold">v{CURRENT_VERSION}</span> después de {CHANGELOG_ENTRIES.length} cambios desde el inicio del proyecto.
            </p>
          </div>
        </details>
      </header>

      {filtered.length === 0 && (
        <div className="py-16 text-center text-slate-500 text-sm">
          No hay entradas que coincidan con los filtros aplicados.
        </div>
      )}

      <div className="space-y-12">
        {grouped.map((group, gi) => (
          <section key={gi}>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-5 capitalize">
              {group.label}
            </h2>
            <div className="space-y-4 relative">
              <div
                className="absolute left-3 top-2 bottom-2 w-px bg-slate-200 hidden sm:block"
                aria-hidden
              />
              {group.entries.map((entry, ei) => {
                const meta = TYPE_META[entry.type] ?? TYPE_META.improvement;
                return (
                  <article
                    key={ei}
                    className="relative bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 sm:ml-10 shadow-sm"
                  >
                    <span
                      className={`hidden sm:block absolute -left-[33px] top-7 w-3 h-3 rounded-full ring-4 ring-slate-50 ${meta.dotClass}`}
                      aria-hidden
                    />
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${meta.badgeClass}`}
                      >
                        {meta.label}
                      </span>
                      <time className="text-xs text-slate-500 font-mono">
                        {formatDate(entry.date)}
                      </time>
                      {entry.version && (
                        <span className="text-[10px] font-mono font-semibold tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          v{entry.version}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 leading-snug">
                      {entry.title}
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed">{entry.summary}</p>
                    {entry.details && entry.details.length > 0 && (
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {entry.details.map((d, di) => (
                          <li key={di} className="flex gap-2.5">
                            <span className="text-slate-300 mt-1.5 shrink-0">•</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function SummaryChip({
  label,
  count,
  className,
}: {
  label: string;
  count: number;
  className: string;
}) {
  return (
    <span
      className={`text-xs font-semibold px-3 py-1.5 rounded-full border border-transparent ${className}`}
    >
      {label}: <span className="font-bold">{count}</span>
    </span>
  );
}
